import React, {
  useState,
  useEffect,
  useReducer,
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  memo,
} from 'react'
import { useSelector } from 'react-redux'
import { Icon } from 'components/forms/InlineEdit/Icon'
import { Select, ISelectOption } from 'components/forms/Selects'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { RadioOption } from 'transfix-ui/components/RadioOption'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { Typography } from 'transfix-ui/core'
import { APPOINTMENT_TYPES } from 'types'
import { ValidationError } from 'yup'
import BookingInfo, {
  PhoneEntry,
} from 'apps/booking/components/CoverLoads/CarrierEntryForm/BookingInfo'
import {
  ISelectedDriverFields,
  IAssignDriverFields,
  CoverDriverRequestFields,
  INewDriverFields,
} from 'apps/booking/types'
import {
  userTypeOptions,
  userRoleOptions,
  DRIVER_TYPES_DISPLAY,
} from 'apps/booking/utils/coverLoad'
import { selectCoverLoad } from 'apps/booking/utils/selectors/coverLoad'
import {
  selectCurrentStopAppointment,
  selectShipment,
  selectShipmentCoveredBid,
} from 'apps/booking/utils/selectors/shipment'
import {
  requiredFields,
  vinCheckSchema,
  phoneSchema,
  emailCheckSchema,
} from 'apps/booking/utils/validationSchema'
import TruckAutosuggest from 'apps/marketplace/components/TruckAutosuggest'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { USER_DRIVER_TYPES } from 'apps/user/types/user'
import { TextInput } from 'components/atoms'
import { DatePicker } from 'components/forms'
import { ButtonGroup, TimeInput, Tag, TAG_STYLE, TAG_SIZE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import formatPhone from 'utils/formatPhone'
import { getIsLate } from 'utils/trackingHelpers'
import { FORMATS } from 'constants/datetime'
import { LATE_REASONS } from 'constants/lateReasons'
import { IAutocomplete } from '../CarrierEntryForm/CarrierInfo'
import DriverInfo, { DriverTractorVin, DriverSelect } from './DriverInfo'
import DriverLocation, { PickupDate } from './DriverLocation'

enum DRIVER_FORM {
  NEW = 'NEW',
  SELECTED = 'SELECTED',
}

const defaultUpdateFields = {
  truckLocation: {},
  vin: '',
  truckNumber: '',
  etaDate: '',
  etaTime: '',
  lateReason: '',
  manualEta: '',
  notesBody: '',
  callbackDate: '',
  callbackTime: '',
}

interface FieldsReducer {
  field: string
  value?: string | ILocationSuggestion | ISelectedDriverFields | INewDriverFields
}

const fieldsReducer = (state: CoverDriverRequestFields, { field, value }: FieldsReducer) => ({
  ...state,
  [field]: value,
})

const defaultValidateFields = {
  ...defaultUpdateFields,
  truckLocation: '',
}

export interface ValidateReducer {
  field: string
  error: string
}

type GenericObject = {
  [key: string]:
    | string
    | {
        [key: string]: string | ISelectedDriverFields
      }
    | undefined
} & CoverDriverRequestFields

const validateReducer = (state: any, { error, field }: ValidateReducer) => ({
  ...state,
  [field]: error,
})

interface IDriverAssignForm {
  setUpdateFields: (fields?: IAssignDriverFields) => void
  setAssignNow: () => void
  setCallbackReminder: () => void
  assignNow: boolean
  callbackRecommended: boolean
}

const RECOMMENDED_CALLBACK_TIME = '4:00pm'

const DriverAssignForm = memo<IDriverAssignForm>(
  ({ assignNow, callbackRecommended, setAssignNow, setCallbackReminder, setUpdateFields }) => {
    const { trackEvent } = useMPAnalytics()
    const coveredBid = useSelector(selectShipmentCoveredBid)
    const coverload = useSelector(selectCoverLoad)
    const shipment = useSelector(selectShipment)

    const [isLateEta, setIsLateEta] = useState(false)
    const [driverFormView, setDriverFormView] = useState<DRIVER_FORM>(DRIVER_FORM.SELECTED)
    const [selectedDriverEdit, setSelectedDriverEdit] = useState(false)
    const [showUserRole, setShowUserRole] = useState(false)
    const [fields, dispatchFields] = useReducer(fieldsReducer, defaultUpdateFields)
    const [errors, dispatchErrors] = useReducer(validateReducer, defaultValidateFields)

    const clearErrors = (field: string) => {
      if (errors[field]) {
        dispatchErrors({ field, error: '' })
      }
    }

    /** Form states */
    const saveCallbackReminderCustom = !assignNow && !callbackRecommended
    const saveSelectedDriver = assignNow && driverFormView === DRIVER_FORM.SELECTED
    const saveNewDriver = assignNow && driverFormView === DRIVER_FORM.NEW

    const changeAssignNowHandler = () => setAssignNow()
    const changeCallbackOptionHandler = () => setCallbackReminder()
    const selectDriverFormHandler = (value: string) => {
      setDriverFormView(value as DRIVER_FORM)

      clearErrors('cellPhone')
      clearErrors('email')
    }

    const dispatchNewDriver = ({ field, value }: FieldsReducer) =>
      dispatchFields({
        field: 'newDriver',
        value: {
          ...fields.newDriver,
          [field]: value,
        } as INewDriverFields,
      })

    const dispatchSelectedDriver = ({ field, value }: FieldsReducer) =>
      dispatchFields({
        field: 'selectedDriver',
        value: {
          ...fields?.selectedDriver,
          [field]: value,
        } as INewDriverFields,
      })

    /** Formatting + defaults */
    const { appointmentType, endAt, startAt: scheduledEta, timezone } = useSelector(
      selectCurrentStopAppointment
    )
    const today = moment()
    const isSameDayOrBefore = moment(scheduledEta).isSameOrBefore(today, 'day')
    const showCustomCallbackInputs = !callbackRecommended || isSameDayOrBefore

    const callbackDefaults = {
      day: moment(scheduledEta).subtract(1, 'days').format(FORMATS.dateFormat),
      time: moment
        .tz(RECOMMENDED_CALLBACK_TIME, FORMATS.militaryTimeFormatShort12H, FORMATS.defaultTimeZone)
        .format(FORMATS.timeFormat),
    }
    const dateTime = `${fields?.etaDate} ${fields?.etaTime}`

    const { newDriver, selectedDriver } = fields
    const driverFullName = selectedDriver
      ? selectedDriver.name
      : [newDriver?.firstName, newDriver?.lastName].join(' ').trim()
    const driver = selectedDriver || newDriver
    const carrierId = coveredBid?.carrierApplicationId || coverload?.carrierApplicationId

    const formattedNoteBody = () => {
      const carrier = coveredBid?.carrierApplicationName || coverload?.carrierApplicationName
      const carrierLink = `#CL [${carrier}](${__ROOT__}/carriers/${carrierId})`
      const coverAmount = Boolean(coverload?.amount) && `$${coverload?.amount}`
      const vinText = Boolean(fields.vin) && `VIN: ${fields.vin}`
      const truckNumber = Boolean(fields.truckNumber) && `Truck No.: ${fields.truckNumber}`
      const { city, state } = fields.truckLocation
      const cityText = assignNow && `Empty in ${city}, ${state}`
      const formattedTimeFormatShort = moment
        .tz(dateTime, FORMATS.datetime12HInputFormat, timezone)
        .format(FORMATS.timeFormatShort)
      const etaText = assignNow && `ETA on ${fields.etaDate} at ${formattedTimeFormatShort}`
      const finalDispatcherName = fields.carrierContactName
      const contactPhone = fields.carrierContactPhone
      const driverCellPhone = formatPhone(driver?.cellPhone)

      const noteBody = [
        carrierLink,
        coverAmount,
        cityText,
        finalDispatcherName,
        contactPhone,
        vinText,
        truckNumber,
        ...(assignNow ? [driverFullName, driverCellPhone] : []),
        etaText,
      ]

      return noteBody.filter(Boolean).join(' · ')
    }

    let noteCallbackDate = callbackDefaults.day
    let noteCallbackTime = callbackDefaults.time

    const { callbackDate, callbackTime } = fields
    const hasCallbackReminder = callbackDate && callbackTime && !assignNow
    if (hasCallbackReminder) {
      noteCallbackDate = moment(callbackDate).format(FORMATS.dateFormat)
      noteCallbackTime = moment
        .tz(callbackTime, FORMATS.militaryTimeFormatShort12H, timezone)
        .format(FORMATS.militaryTimeFormat)
    }

    const checkRequiredFieldValues = (requiredField: string, formFields: GenericObject): boolean =>
      Boolean(formFields[requiredField])

    // Validate all required fields are filled + no errors
    // TODO: TypeScript errors on index signatures
    const checkRequiredValues = (formFields: any): boolean => {
      const {
        assignNewDefault,
        assignSelected,
        callbackCustom,
        callbackReminder,
        ownerOperatorRequired,
      } = requiredFields

      // Flatten nested object fields to run required fields check
      const checkFormFields = {
        ...formFields,
        ...(saveSelectedDriver && formFields?.selectedDriver),
        ...(saveNewDriver && formFields?.newDriver),
      }

      // Callback Reminder:
      // By default, set to Callback Recommended (Pickup is not same day)
      let checkFields = callbackReminder

      // Callback Custom
      if (saveCallbackReminderCustom) {
        checkFields = callbackCustom
      }

      if (saveSelectedDriver) {
        // Assign Driver Now:
        // Assign Now - Select a driver
        checkFields = assignSelected
      }

      // Assign Now - Selected driver + role
      if (saveSelectedDriver && showUserRole) {
        checkFields = [...assignSelected, ...ownerOperatorRequired]
      }

      // Assign Now - New driver
      if (saveNewDriver) {
        checkFields = assignNewDefault
      }

      // Assign Now - New driver + role
      if (saveNewDriver && showUserRole) {
        checkFields = [...assignNewDefault, ...ownerOperatorRequired]
      }

      // Add additional required fields
      checkFields = [
        ...checkFields,
        ...(isLateEta ? ['lateReason'] : []),
        ...(fields?.vin.length ? ['vin'] : []),
      ]

      const requiredValuesCheck = checkFields.every(field =>
        checkRequiredFieldValues(field, checkFormFields)
      )

      // For AssignNow = check errors for each driver field: `new` `select`
      const hasNoErrors = checkFields.every((field: string) => {
        return !errors[field]
      })

      return requiredValuesCheck && hasNoErrors
    }

    /** Validation handlers */
    const validateVin = (event: FocusEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (!value) {
        clearErrors('vin')

        return
      }

      vinCheckSchema
        .validate(value)
        .then(() => clearErrors('vin'))
        .catch((e: ValidationError) => {
          dispatchErrors({ field: 'vin', error: e.message })
        })
    }

    const validatePhone = (phoneNumber: string) => {
      const field = assignNow ? 'cellPhone' : 'carrierContactPhone'
      phoneSchema
        .validate(phoneNumber)
        .then(() => clearErrors(field))
        .catch((e: ValidationError) => dispatchErrors({ field, error: e.message }))
    }

    const validateEmail = (event: FocusEvent<HTMLInputElement>) => {
      const { id: field, value } = event.target
      emailCheckSchema
        .validate(value)
        .then(() => clearErrors(field))
        .catch((e: ValidationError) => dispatchErrors({ field, error: e.message }))
    }

    // Validate and format payload fields before lifting state up to submit payload
    useEffect(() => {
      const callbackSettings = {
        callbackDate: noteCallbackDate,
        callbackTime: noteCallbackTime,
      }

      const fieldsWithCallback = {
        ...fields,
        ...(hasCallbackReminder && {
          ...callbackSettings,
        }),
      }

      const isValid = checkRequiredValues(fieldsWithCallback)

      if (isValid) {
        const fieldsWithNote = {
          ...fieldsWithCallback,
          notesBody: formattedNoteBody(),
        }

        setUpdateFields(fieldsWithNote)
      } else {
        setUpdateFields(undefined)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, errors, driverFormView, assignNow, setUpdateFields])

    useEffect(() => {
      // Clear previously saved view's form
      const clearOtherDriverFields = saveNewDriver ? 'selectedDriver' : 'newDriver'
      dispatchFields({ field: clearOtherDriverFields, value: undefined })
    }, [saveNewDriver])

    useEffect(() => {
      const isDriverUserType = fields?.newDriver?.userType === USER_DRIVER_TYPES.driver
      const setEmployeeRole = !isDriverUserType ? 'employee' : ''

      dispatchNewDriver({ field: 'role', value: setEmployeeRole })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUserRole])

    // Validates phone number whenenver value changes: pre-populated + input entry
    useEffect(() => {
      if (fields.carrierContactPhone) {
        validatePhone(fields.carrierContactPhone)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.carrierContactPhone])

    // Pre-populate contact fields
    useEffect(() => {
      const formattedPhone = formatPhone(
        coverload?.carrierContactPhone || coveredBid?.carrierContactPhone
      )
      dispatchFields({
        field: 'carrierContactName',
        value: coverload?.carrierContactName || coveredBid?.carrierContactName || '',
      })
      dispatchFields({
        field: 'carrierContactPhone',
        value: formattedPhone,
      })
      dispatchFields({
        field: 'carrierContactPhoneExt',
        value: coverload?.carrierContactPhoneExt || coveredBid?.carrierContactPhoneExt || '',
      })
    }, [coverload, coveredBid])

    /**
     * Determine late eta
     * Set manual eta
     */
    useEffect(() => {
      if (!dateTime.trim()) return

      const startAtEtaDateTime = moment(scheduledEta).tz(timezone).format()
      const endAtEtaDateTime = moment(endAt).tz(timezone).subtract(2, 'hours').format()
      const scheduledEtaDateTime =
        appointmentType === APPOINTMENT_TYPES.FCFS ? endAtEtaDateTime : startAtEtaDateTime
      const manualEta = moment
        .tz(dateTime, FORMATS.datetime12HInputFormat, timezone)
        .format(FORMATS.iso8601Format)
      const isLate = Boolean(getIsLate(moment(manualEta), moment(scheduledEtaDateTime)))
      setIsLateEta(isLate)

      if (!isLate && fields.lateReason.length) {
        dispatchFields({ field: 'lateReason', value: '' })
      }

      dispatchFields({ field: 'manualEta', value: manualEta })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateTime, appointmentType, endAt, scheduledEta, timezone])

    /** Form Handlers */
    const dateChangeHandler = (selected: string[], id?: string) => {
      const selectedDate = selected[0]
      if (!selectedDate || !id) return

      const value = moment(selectedDate).format(FORMATS.dateFormat)
      dispatchFields({ field: id, value })
    }

    const changeTimeInputHandler = (change: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { id, value },
      } = change
      if (!value.length) return

      dispatchFields({ field: id, value })
    }

    const changeInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
      const { name: field, value } = event.target
      dispatchFields({ field, value })
    }

    const changeTruckInputHandler = (val?: string) =>
      dispatchFields({ field: 'truckNumber', value: val })

    const blurTruckInputHandler = () =>
      trackEvent('Booking', 'CHANGE_TRUCK_NUMBER', {
        shipmentId: shipment?.id,
        updatedTruckNumber: fields?.truckNumber,
        context: 'Cover Load Modal',
      })

    const changeLocationHandler = (value: ILocationSuggestion) => {
      dispatchFields({ field: 'truckLocation', value })
    }

    const changeDriverHandler = (selected: IAutocomplete) => {
      const { fullSelection } = selected

      dispatchFields({ field: 'selectedDriver', value: fullSelection as ISelectedDriverFields })
    }

    const changeSelectHandler = (option: ISelectOption<string>) =>
      dispatchFields({ field: 'lateReason', value: option.value })

    /** New Driver Form Handlers */
    const selectNewDriverUserTypeHandler = (option: ISelectOption<string>) => {
      const isOwnerOperator = option.value === USER_DRIVER_TYPES.ownerOperator
      setShowUserRole(isOwnerOperator)
      dispatchNewDriver({ field: 'userType', value: option.value })
    }

    const selectNewDriverUserRoleHandler = (option: ISelectOption<string>) =>
      dispatchNewDriver({ field: 'role', value: option.value })

    const changeNewDriverInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
      const { name: field, value } = event.target
      dispatchNewDriver({ field, value })
      if (field === 'cellPhone') {
        validatePhone(value)
      }
    }

    /** Selected Driver Form Handlers */
    const selectSelectedDriverUserTypeHandler = (option: ISelectOption<string>) => {
      const isOwnerOperator = option.value === USER_DRIVER_TYPES.ownerOperator
      setShowUserRole(isOwnerOperator)
      dispatchSelectedDriver({ field: 'userType', value: option.value })
    }

    const selectSelectedDriverRoleHandler = (option: ISelectOption<string>) => {
      dispatchSelectedDriver({ field: 'role', value: option.value })
    }

    const changeSelectedDriverInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
      const { name: field, value } = event.target
      dispatchSelectedDriver({ field, value })
      if (field === 'cellPhone') {
        validatePhone(value)
      }
    }

    const editDriverHandler = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setSelectedDriverEdit(!selectedDriverEdit)
    }

    return (
      <div className='fadeIn'>
        <span>Step 2 of 2</span>
        <form data-test='driver-form'>
          <section>
            <Typography variant='h2'>Assign a Driver</Typography>
            <p>
              A Carrier Experience Rep will use this information to contact the driver and help them
              get tracking on the Transfix app.
            </p>

            <div className='mv6'>
              <RadioOption
                id='assign-now'
                name='assignDriver'
                value='assign-now'
                selected={assignNow}
                className='mt1'
                dataTest='assign-now'
                onChange={changeAssignNowHandler}>
                <div>
                  <span className='b'>Assign Driver Now</span>
                  <p>Dispatcher has a driver assigned. Assign a driver and provide their ETA.</p>
                </div>
              </RadioOption>
              <RadioOption
                id='assign-later'
                name='assignDriver'
                value='assign-later'
                dataTest='assign-later'
                selected={!assignNow}
                className='mt1'
                onChange={changeAssignNowHandler}>
                <div>
                  <span className='b'>Assign Driver Later</span>
                  <p>Dispatcher does not have driver assigned. Set a reminder.</p>
                </div>
              </RadioOption>
            </div>

            {assignNow ? (
              <>
                <DriverInfo>
                  <ButtonGroup
                    className='dialog-medium mb5'
                    value={driverFormView}
                    hideLabel
                    onChange={selectDriverFormHandler}
                    dataTest='assign-driver'>
                    <Button value={DRIVER_FORM.SELECTED}>Existing Driver</Button>
                    <Button value={DRIVER_FORM.NEW}>New Driver</Button>
                  </ButtonGroup>
                  {/* Section: Select Driver */}
                  {driverFormView === DRIVER_FORM.SELECTED && (
                    <>
                      <DriverSelect
                        carrierId={carrierId}
                        changeDriverHandler={changeDriverHandler}
                        value={fields.selectedDriver?.name}
                      />
                      {fields?.selectedDriver?.id && (
                        <div className='pa4 pt6 mt4 coolGray1 relative'>
                          <span className='absolute driver-form__edit'>
                            <Icon editing={selectedDriverEdit} clickHandler={editDriverHandler} />
                          </span>
                          {!selectedDriverEdit ? (
                            <>
                              <div className='b mb1'>User Type</div>
                              <div className='mb4'>
                                {
                                  DRIVER_TYPES_DISPLAY[
                                    fields?.selectedDriver?.userType as USER_DRIVER_TYPES
                                  ]
                                }
                              </div>
                              <div className='b mb1'>Role</div>
                              <div className='mb4'>{capitalize(fields?.selectedDriver?.role)}</div>
                              <div className='b mb1'>Cell Phone</div>
                              <div className='mb4'>
                                {formatPhone(fields?.selectedDriver?.cellPhone)}
                              </div>
                            </>
                          ) : (
                            <>
                              <Select
                                label='User Type'
                                id='userType'
                                required
                                dataTest='selected-driver-user-type'
                                options={userTypeOptions}
                                value={fields.selectedDriver?.userType}
                                onChange={selectSelectedDriverUserTypeHandler}
                              />
                              <div className='mt4' />
                              {showUserRole && (
                                <Select
                                  label='Role'
                                  required
                                  value={fields?.selectedDriver?.role}
                                  dataTest='selected-driver-role'
                                  id='role'
                                  options={userRoleOptions}
                                  onChange={selectSelectedDriverRoleHandler}
                                />
                              )}
                              <PhoneInput
                                onChange={changeSelectedDriverInputHandler}
                                dataTest='selected-driver-cellphone'
                                name='cellPhone'
                                value={fields?.selectedDriver?.cellPhone}
                                required
                                id='cellPhone'
                                label='Cell Phone'
                                className='mv4 dialog-medium'
                                clearable={false}
                                invalid={Boolean(errors?.cellPhone)}
                                validationMessage={errors?.cellPhone}
                              />
                              {showUserRole && (
                                <TextInput
                                  label='Email'
                                  required
                                  value={fields?.selectedDriver?.email}
                                  dataTest='selected-driver-email'
                                  id='email'
                                  name='email'
                                  onChange={changeSelectedDriverInputHandler}
                                  onBlur={validateEmail}
                                  invalid={Boolean(errors?.email)}
                                  validationMessage={errors?.email}
                                  className='mb4 pb2'
                                  clearable={false}
                                />
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {/* Section: Add New Driver */}
                  {driverFormView === DRIVER_FORM.NEW && (
                    <div className='pa5 pb6 coolGray1'>
                      <div className='b mb4'>Create a New User</div>
                      <Select
                        label='User Type'
                        id='userType'
                        required
                        dataTest='new-driver-user-type'
                        options={userTypeOptions}
                        value={fields.newDriver?.userType}
                        onChange={selectNewDriverUserTypeHandler}
                      />
                      <PhoneInput
                        onChange={changeNewDriverInputHandler}
                        dataTest='new-driver-cellphone'
                        name='cellPhone'
                        value={fields?.newDriver?.cellPhone}
                        required
                        label='Cell Phone'
                        className='mt4 dialog-medium'
                        clearable={false}
                        invalid={Boolean(errors?.cellPhone)}
                        validationMessage={errors?.cellPhone}
                      />
                      {showUserRole && (
                        <TextInput
                          label='Email'
                          required
                          value={fields?.newDriver?.email}
                          dataTest='new-driver-email'
                          id='email'
                          name='email'
                          onChange={changeNewDriverInputHandler}
                          onBlur={validateEmail}
                          invalid={Boolean(errors?.email)}
                          validationMessage={errors?.email}
                          className='mt4'
                          clearable={false}
                        />
                      )}
                      <TextInput
                        label='First Name'
                        required
                        value={fields?.newDriver?.firstName}
                        dataTest='new-driver-first-name'
                        id='firstName'
                        name='firstName'
                        onChange={changeNewDriverInputHandler}
                        className='mt4'
                        clearable={false}
                      />
                      <TextInput
                        label='Last Name'
                        required
                        value={fields?.newDriver?.lastName}
                        dataTest='new-driver-last-name'
                        id='lastName'
                        name='lastName'
                        onChange={changeNewDriverInputHandler}
                        className='mv4'
                        clearable={false}
                      />
                      {showUserRole && (
                        <Select
                          label='Role'
                          id='role'
                          required
                          dataTest='new-driver-role'
                          options={userRoleOptions}
                          value={fields.newDriver?.role}
                          onChange={selectNewDriverUserRoleHandler}
                        />
                      )}
                      {fields?.newDriver?.userType === USER_DRIVER_TYPES.driver && (
                        <>
                          <div className='label required mb1'>Role</div>
                          <div className='mb4'>Employee</div>
                        </>
                      )}
                    </div>
                  )}
                  <div className='flex mt4'>
                    <DriverTractorVin
                      onChange={changeInputHandler}
                      onBlur={validateVin}
                      value={fields?.vin}
                      vinError={errors.vin}
                    />
                    {carrierId && (
                      <TruckAutosuggest
                        carrierId={carrierId}
                        value={fields?.truckNumber}
                        onChange={changeTruckInputHandler}
                        onBlur={blurTruckInputHandler}
                        large={false}
                        classNames='ml4 truck-autosuggest-container'
                        label='Truck Number'
                      />
                    )}
                  </div>
                </DriverInfo>
                {/* Section: Driver Location + ETA entry */}
                <DriverLocation>
                  <SuggestLocation
                    label='Empty Location'
                    required
                    value={fields?.truckLocation}
                    dataTest='select-location'
                    placeholder='Choose Location'
                    onChange={changeLocationHandler}
                  />
                  <div className='mt4 flex'>
                    <PickupDate onDateChange={dateChangeHandler} />
                    <TimeInput
                      className='ml2'
                      onChange={changeTimeInputHandler}
                      id='etaTime'
                      dataTest='input-eta-time'
                      value={fields?.etaTime}
                      label='ETA Time'
                      required
                    />
                  </div>
                  {isLateEta && (
                    <div className='mt4'>
                      <Select
                        label='Late Reason'
                        required
                        dataTest='select-late-reason'
                        options={LATE_REASONS}
                        value={fields?.lateReason}
                        onChange={changeSelectHandler}
                      />
                    </div>
                  )}
                </DriverLocation>
              </>
            ) : (
              // Section: Callback Reminder
              <section>
                <div className='b mv2'>Choose callback reminder option:</div>
                {!isSameDayOrBefore && (
                  <RadioOption
                    selected={callbackRecommended}
                    name='callback'
                    value='recommended'
                    onChange={changeCallbackOptionHandler}
                    id='callback-default'>
                    <div className='ml1'>
                      Send me an email <b>day before the pickup</b> on{' '}
                      <b>
                        {callbackDefaults.day}, {callbackDefaults.time}
                      </b>
                      <Tag
                        tagStyle={TAG_STYLE.success}
                        size={TAG_SIZE.xsmall}
                        label='Recommended'
                        className='ml2'
                        style={{ display: 'inline' }}
                      />
                    </div>
                  </RadioOption>
                )}
                <RadioOption
                  name='callback'
                  value='custom'
                  selected={showCustomCallbackInputs}
                  onChange={changeCallbackOptionHandler}
                  className='mt4'
                  id='callback-custom'>
                  <div className='ml1'>Set a custom date and time</div>
                </RadioOption>
                {showCustomCallbackInputs && (
                  <div className='mt4 ml6 flex'>
                    <DatePicker
                      onDateChange={dateChangeHandler}
                      id='callbackDate'
                      dataTest='input-callback-date'
                    />
                    <TimeInput
                      className='ml2'
                      onChange={changeTimeInputHandler}
                      id='callbackTime'
                      dataTest='input-callback-time'
                      value={fields?.callbackTime}
                      required
                    />
                  </div>
                )}
                {/* Section: Carrier Contact */}
                <BookingInfo>
                  <TextInput
                    required
                    onChange={changeInputHandler}
                    name='carrierContactName'
                    data-test='carrier-contact-name'
                    label='Contact Name'
                    clearable={false}
                    value={fields?.carrierContactName}
                  />
                  <PhoneEntry
                    changePhoneHandler={changeInputHandler}
                    changePhoneExtHandler={changeInputHandler}
                    contactPhone={fields?.carrierContactPhone}
                    contactPhoneExt={fields?.carrierContactPhoneExt}
                    contactPhoneError={errors.carrierContactPhone}
                  />
                </BookingInfo>
              </section>
            )}
          </section>
        </form>
      </div>
    )
  }
)

export default DriverAssignForm
